import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  FileCheck2,
  MessageCircle,
  Package,
  Plane,
  Scale,
} from "lucide-react";
import { PRODUCTS, PRODUCT_PAGES, trackEvent, waLink } from "@/lib/vgt";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/landing/Reveal";
import { Footer } from "@/components/landing/Footer";
import { FloatingWhatsApp } from "@/components/landing/FloatingWhatsApp";

const SITE = "https://vanillaglobaltransindo.com";

const EXPORT_POINTS = [
  {
    icon: Package,
    title: "Vacuum Packaging",
    desc: "Your order is double vacuum-sealed in food-grade foil pouches and secured in rigid master cartons.",
  },
  {
    icon: Scale,
    title: "MOQ from 1 kg",
    desc: "Evaluation orders from 1 kg up to full commercial volumes, with samples available on request.",
  },
  {
    icon: FileCheck2,
    title: "Export Documents",
    desc: "Phytosanitary Certificate, Certificate of Analysis, Commercial Invoice and Packing List where applicable.",
  },
  {
    icon: Plane,
    title: "Express Air Freight",
    desc: "Typically 4-8 business days to the USA, Europe, the Middle East, Asia and Australia after dispatch.",
  },
];

export default function ProductPage() {
  const { slug } = useParams();
  const page = PRODUCT_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;
  const product = PRODUCTS.find((p) => p.id === page.productId);

  const waMsg = `Hello VGT team, I'd like a quotation for ${product.formValue}.\nEstimated quantity: \nDestination country: `;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: page.h1,
    description: page.seoDescription,
    image: `${SITE}${product.image}`,
    url: `${SITE}/products/${slug}`,
    category: page.category,
    brand: { "@type": "Brand", name: "Vanilla Global Transindo" },
  };

  return (
    <div data-testid="product-page" className="bg-[#FDFBF7] text-[#2C1A0C] font-sans min-h-screen">
      <Seo
        title={page.seoTitle}
        description={page.seoDescription}
        path={`/products/${slug}`}
        image={product.image}
        jsonLd={jsonLd}
      />

      <header className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur border-b border-[#E6DEC3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="product-brand-link" className="flex items-center gap-3">
            <img
              src="/images/vgt-logo.jpg"
              alt="Vanilla Global Transindo logo"
              className="w-10 h-10 rounded-full object-cover ring-1 ring-[#E6DEC3]"
            />
            <span className="font-serif font-bold text-base sm:text-lg">Vanilla Global Transindo</span>
          </Link>
          <Link
            to="/"
            data-testid="product-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E4F3E] hover:text-[#1B3B2B] transition-colors"
          >
            <ArrowLeft size={15} />
            Back to site
          </Link>
        </div>
      </header>

      <main>
        <section className="py-16 sm:py-24 px-4 sm:px-8">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#5C3A21]">
                Our Products · {product.tag}
              </p>
              <h1 className="mt-4 font-serif font-bold text-4xl sm:text-5xl text-[#2C1A0C] leading-tight">
                {page.h1}
              </h1>
              <p className="mt-4 font-serif italic text-lg text-[#D4A359]">{page.tagline}</p>
              {page.intro.map((para) => (
                <p key={para.slice(0, 24)} className="mt-4 text-sm sm:text-base text-[#6E5B4F] leading-relaxed">
                  {para}
                </p>
              ))}
              <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
                <a
                  data-testid="product-quote-cta"
                  href="/#quotation"
                  className="group inline-flex items-center justify-center gap-2.5 bg-[#2E4F3E] hover:bg-[#1B3B2B] text-[#FDFBF7] font-semibold rounded-full px-7 py-3.5 text-sm transition-colors"
                >
                  Request a Quotation
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  data-testid="product-whatsapp-cta"
                  href={waLink(waMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: `product_${slug}` })}
                  className="inline-flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1EAE52] text-white font-semibold rounded-full px-7 py-3.5 text-sm transition-colors"
                >
                  <MessageCircle size={16} />
                  Ask on WhatsApp
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="rounded-3xl overflow-hidden border border-[#E6DEC3] shadow-xl">
                <img
                  src={product.image}
                  alt={`${product.name} — Indonesian vanilla by Vanilla Global Transindo`}
                  className={`w-full aspect-[4/3] object-cover ${product.imageClass || ""}`}
                />
              </div>
              <dl className="mt-6 bg-white border border-[#E6DEC3] rounded-3xl p-6 sm:p-8 space-y-3">
                {product.specs.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 text-sm border-b border-[#F5EFE6] pb-2.5 last:border-0 last:pb-0">
                    <dt className="font-mono text-[11px] tracking-wider uppercase text-[#6E5B4F] pt-0.5">{k}</dt>
                    <dd className="text-right font-semibold text-[#2C1A0C]">{v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </section>

        <section className="pb-16 sm:pb-24 px-4 sm:px-8">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <h2 className="font-serif font-bold text-2xl sm:text-3xl text-[#2C1A0C]">Best For</h2>
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {page.applications.map((a) => (
                  <div
                    key={a}
                    className="flex items-center gap-3 bg-white border border-[#E6DEC3] rounded-2xl px-5 py-4"
                  >
                    <BadgeCheck size={18} className="text-[#2E4F3E] shrink-0" />
                    <span className="text-sm font-semibold text-[#2C1A0C]">{a}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 sm:py-20 px-4 sm:px-8 bg-[#1C120C]">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <h2 className="font-serif font-bold text-2xl sm:text-3xl text-[#FAF6EE]">
                Ordering &amp; Export
              </h2>
              <p className="mt-3 text-sm text-[#FAF6EE]/60 max-w-2xl leading-relaxed">
                Every order ships with the same export-grade standard, whatever the volume.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {EXPORT_POINTS.map((point) => (
                  <div key={point.title} className="bg-[#2A1B12] border border-[#3D2B1E] rounded-2xl p-6">
                    <point.icon size={20} className="text-[#D4A359]" />
                    <h3 className="mt-4 font-semibold text-[#FAF6EE] text-sm">{point.title}</h3>
                    <p className="mt-2 text-xs text-[#FAF6EE]/60 leading-relaxed">{point.desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 sm:py-20 px-4 sm:px-8">
          <Reveal>
            <div className="max-w-4xl mx-auto text-center bg-white border border-[#E6DEC3] rounded-3xl p-8 sm:p-12 shadow-xl">
              <h2 className="font-serif font-bold text-2xl sm:text-3xl text-[#2C1A0C]">
                Ready to order {product.name}?
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[#6E5B4F] max-w-xl mx-auto leading-relaxed">
                Tell us your quantity and destination — our export team replies with pricing and shipping
                options, usually within one business day.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3.5">
                <a
                  data-testid="product-bottom-quote-cta"
                  href="/#quotation"
                  className="group inline-flex items-center justify-center gap-2.5 bg-[#2E4F3E] hover:bg-[#1B3B2B] text-[#FDFBF7] font-semibold rounded-full px-7 py-3.5 text-sm transition-colors"
                >
                  Request a Quotation
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <Link
                  data-testid="product-all-products-link"
                  to="/#products"
                  className="inline-flex items-center justify-center gap-2 border border-[#E6DEC3] hover:border-[#2E4F3E] text-[#2C1A0C] font-semibold rounded-full px-7 py-3.5 text-sm transition-colors"
                >
                  Compare all products
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
